<template>
  <v-container fluid class="my-5">
    <div class="d-flex justify-space-between align-center flex-wrap mb-4">
      <div>
        <h2 class="subheading grey--text">Invitaciones de oficina</h2>
        <p class="grey--text text--darken-1 mb-0">Registra y controla las invitaciones emitidas por las áreas de oficina.</p>
      </div>
      <v-btn color="primary" @click="openDialog()" :loading="loading"><v-icon left>mdi-plus</v-icon>Nueva invitación</v-btn>
    </div>

    <v-card class="elevation-2">
      <v-data-table :headers="headers" :items="invitations" :loading="loading" :no-data-text="'No hay invitaciones registradas'">
        <template v-slot:item.confirmed="{ item }"><v-chip small :color="item.confirmed ? 'success' : 'warning'" text-color="white">{{ item.confirmed ? 'Confirmada' : 'Pendiente' }}</v-chip></template>
        <template v-slot:item.canceled="{ item }"><v-chip small :color="item.canceled ? 'error' : 'success'" text-color="white">{{ item.canceled ? 'Cancelada' : 'Activa' }}</v-chip></template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }"><v-icon small v-on="on" @click="viewDetails(item)">mdi-eye-outline</v-icon></template>
            <span>Ver detalles</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" persistent max-width="720" scrollable>
      <v-form @submit.prevent="saveInvitation()" ref="form">
        <v-card>
          <v-toolbar dark color="primary" flat>
            <v-icon left>mdi-email-plus-outline</v-icon>
            <v-toolbar-title>Nueva invitación de oficina</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>

          <v-card-text class="pt-6" style="max-height: 70vh;">
            <div class="text-overline grey--text text--darken-1 mb-1">Detalles de la invitación</div>
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model="invitation.subject"
                  label="Asunto o propósito"
                  placeholder="Ej. Revisión de sistema de vigilancia"
                  prepend-inner-icon="mdi-text-box-outline"
                  outlined dense required
                  maxlength="400" counter
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="6">
                <v-select
                  v-model="invitation.typeInv"
                  :items="invitationTypes"
                  label="Tipo de invitación"
                  prepend-inner-icon="mdi-repeat-variant"
                  outlined dense required
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="invitation.dateInv"
                  label="Fecha"
                  type="date"
                  prepend-inner-icon="mdi-calendar"
                  outlined dense required
                ></v-text-field>
              </v-col>

              <v-col cols="12" :sm="invitation.typeInv === 2 ? 6 : 12">
                <v-text-field
                  v-model="invitation.timeInv"
                  label="Hora"
                  type="time"
                  prepend-inner-icon="mdi-clock-outline"
                  outlined dense required
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" v-if="invitation.typeInv === 2">
                <v-text-field
                  v-model="invitation.expiration"
                  label="Fecha de expiración"
                  type="date"
                  prepend-inner-icon="mdi-calendar-end"
                  outlined dense required
                  hint="Requerida para invitaciones recurrentes"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <v-col cols="12" v-if="invitation.typeInv === 2">
                <div class="caption grey--text text--darken-1 mb-1">Días recurrentes</div>
                <v-chip-group v-model="selectedDays" multiple column @change="updateDiary">
                  <v-chip
                    v-for="day in weekDays"
                    :key="day.value"
                    :value="day.value"
                    filter
                    outlined
                    small
                  >{{ day.label }}</v-chip>
                </v-chip-group>
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="invitation.notes"
                  label="Notas adicionales"
                  prepend-inner-icon="mdi-note-text-outline"
                  outlined dense rows="2" auto-grow
                  maxlength="256" counter
                ></v-textarea>
              </v-col>

              <v-col cols="12" sm="6">
                <v-switch
                  v-model="invitation.vehicle"
                  label="Trae vehículo"
                  color="primary"
                  hide-details
                  inset
                ></v-switch>
              </v-col>
              <v-col cols="12" sm="6">
                <v-switch
                  v-model="invitation.supplier"
                  label="Es proveedor"
                  color="primary"
                  hide-details
                  inset
                ></v-switch>
              </v-col>
            </v-row>

            <v-divider class="my-5"></v-divider>

            <div class="d-flex justify-space-between align-center mb-3">
              <div class="text-overline grey--text text--darken-1">Invitados ({{ invitation.guests.length }})</div>
              <v-btn small text color="primary" @click="addGuest()">
                <v-icon left small>mdi-plus</v-icon>Agregar invitado
              </v-btn>
            </div>

            <v-card
              v-for="(guest, index) in invitation.guests"
              :key="index"
              outlined
              class="mb-3 pa-3"
            >
              <div class="d-flex align-center mb-2">
                <v-avatar size="26" color="primary" class="mr-2">
                  <img v-if="guest._avatar" :src="guest._avatar" alt="avatar">
                  <span v-else class="white--text caption">{{ index + 1 }}</span>
                </v-avatar>
                <span class="caption grey--text text--darken-1">Invitado {{ index + 1 }}</span>
                <v-chip v-if="guest._isExistingUser" x-small color="success" text-color="white" class="ml-2">
                  <v-icon left x-small>mdi-check-circle</v-icon>Usuario existente
                </v-chip>
                <v-spacer></v-spacer>
                <v-btn
                  v-if="invitation.guests.length > 1"
                  icon small color="error"
                  @click="removeGuest(index)"
                >
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </div>

              <div class="mb-3" v-if="!guest._isExistingUser">
                <v-text-field
                  v-model="guest._searchText"
                  label="Buscar usuario existente (opcional)"
                  placeholder="Nombre, email o teléfono"
                  prepend-inner-icon="mdi-magnify"
                  outlined dense clearable hide-details
                  :loading="guest._searching"
                  @input="onGuestSearchInput(index, $event)"
                  @click:clear="clearGuestSearch(index)"
                ></v-text-field>

                <v-card v-if="guest._searchResults.length" outlined class="mt-1">
                  <v-list dense>
                    <v-list-item
                      v-for="user in guest._searchResults"
                      :key="user.id"
                      @click="selectExistingUser(index, user)"
                    >
                      <v-list-item-avatar>
                        <img v-if="user.avatar" :src="user.avatar" alt="avatar">
                        <v-icon v-else>mdi-account-circle</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>{{ user.first_name }} {{ user.last_name }}</v-list-item-title>
                        <v-list-item-subtitle>{{ user.email }} · {{ user.cellNumber }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card>
              </div>
              <v-btn v-else small text color="primary" class="mb-3" @click="clearExistingUser(index)">
                <v-icon left small>mdi-close</v-icon>Buscar otro usuario
              </v-btn>

              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="guest.fullNameGuest"
                    label="Nombre completo"
                    prepend-inner-icon="mdi-account-outline"
                    outlined dense required hide-details
                    :readonly="guest._isExistingUser"
                    :filled="guest._isExistingUser"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="guest.email"
                    label="Email"
                    type="email"
                    prepend-inner-icon="mdi-email-outline"
                    outlined dense required hide-details
                    :readonly="guest._isExistingUser"
                    :filled="guest._isExistingUser"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="guest.cellNumber"
                    label="Teléfono"
                    placeholder="+524446374799"
                    prepend-inner-icon="mdi-phone-outline"
                    outlined dense required hide-details
                    :readonly="guest._isExistingUser"
                    :filled="guest._isExistingUser"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card>
          </v-card-text>

          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <v-btn text @click="dialog = false">Cancelar</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" type="submit" :loading="savingInvitation" depressed>
              <v-icon left small>mdi-send-outline</v-icon>Enviar invitación
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <v-dialog v-model="detailDialog" max-width="600" scrollable>
      <v-card v-if="selectedInvitation">
        <v-toolbar dark color="primary" flat>
          <v-icon left>mdi-email-outline</v-icon>
          <v-toolbar-title>Detalle de la invitación</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="pt-5" style="max-height: 75vh;">
          <div class="d-flex align-center mb-4">
            <v-avatar size="48" color="grey lighten-2" class="mr-3">
              <img v-if="selectedInvitation.avatar_guest" :src="selectedInvitation.avatar_guest" alt="avatar">
              <v-icon v-else large>mdi-account-circle</v-icon>
            </v-avatar>
            <div>
              <div class="title">{{ selectedInvitation.guest_name }}</div>
              <div class="grey--text">{{ selectedInvitation.guest_number_phone }}</div>
            </div>
            <v-spacer></v-spacer>
            <v-chip small :color="selectedInvitation.confirmed ? 'success' : 'warning'" text-color="white" class="mb-1">
              {{ selectedInvitation.confirmed ? 'Confirmada' : 'Pendiente' }}
            </v-chip>
          </div>
          <v-chip small :color="selectedInvitation.canceled ? 'error' : 'success'" text-color="white" class="mb-4">
            {{ selectedInvitation.canceled ? 'Cancelada' : 'Activa' }}
          </v-chip>

          <v-divider class="mb-4"></v-divider>

          <div class="text-overline grey--text text--darken-1 mb-1">Invitación</div>
          <v-list dense class="pa-0">
            <v-list-item class="px-0">
              <v-list-item-icon class="mr-3"><v-icon small>mdi-text-box-outline</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>Asunto</v-list-item-subtitle>
                <v-list-item-title>{{ selectedInvitation.subject || '—' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item class="px-0">
              <v-list-item-icon class="mr-3"><v-icon small>mdi-calendar</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>Fecha y hora</v-list-item-subtitle>
                <v-list-item-title>{{ selectedInvitation.date }} · {{ selectedInvitation.hour }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item class="px-0">
              <v-list-item-icon class="mr-3"><v-icon small>mdi-repeat-variant</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>Tipo</v-list-item-subtitle>
                <v-list-item-title>{{ formatType(selectedInvitation.type) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item class="px-0" v-if="selectedInvitation.type === 2">
              <v-list-item-icon class="mr-3"><v-icon small>mdi-calendar-end</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>Expira</v-list-item-subtitle>
                <v-list-item-title>{{ selectedInvitation.expiration || '—' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item class="px-0" v-if="selectedInvitation.type === 2">
              <v-list-item-icon class="mr-3"><v-icon small>mdi-calendar-week</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>Días recurrentes</v-list-item-subtitle>
                <v-list-item-title>{{ formatDiary(selectedInvitation.diary) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item class="px-0">
              <v-list-item-icon class="mr-3"><v-icon small>mdi-car-outline</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>Vehículo / Proveedor</v-list-item-subtitle>
                <v-list-item-title>{{ selectedInvitation.vehicle ? 'Con vehículo' : 'Sin vehículo' }} · {{ selectedInvitation.supplier ? 'Es proveedor' : 'No es proveedor' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item class="px-0" v-if="selectedInvitation.notes">
              <v-list-item-icon class="mr-3"><v-icon small>mdi-note-text-outline</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>Notas</v-list-item-subtitle>
                <v-list-item-title style="white-space: normal;">{{ selectedInvitation.notes }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-divider class="my-4"></v-divider>

          <div class="text-overline grey--text text--darken-1 mb-1">Ubicación</div>
          <v-list dense class="pa-0">
            <v-list-item class="px-0">
              <v-list-item-icon class="mr-3"><v-icon small>mdi-office-building-outline</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>Residencia</v-list-item-subtitle>
                <v-list-item-title>{{ selectedInvitation.housing_unit_name || '—' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item class="px-0">
              <v-list-item-icon class="mr-3"><v-icon small>mdi-map-marker-outline</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>Dirección</v-list-item-subtitle>
                <v-list-item-title>{{ selectedInvitation.house_address || '—' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <template v-if="selectedInvitation.guardhouses_details && selectedInvitation.guardhouses_details.length">
            <v-divider class="my-4"></v-divider>
            <div class="text-overline grey--text text--darken-1 mb-1">Casetas</div>
            <v-chip v-for="(guardhouse, i) in selectedInvitation.guardhouses_details" :key="i" small outlined class="mr-1 mb-1">
              {{ typeof guardhouse === 'string' ? guardhouse : (guardhouse.name || guardhouse.id) }}
            </v-chip>
          </template>

          <v-divider class="my-4"></v-divider>

          <div class="text-overline grey--text text--darken-1 mb-1">Acceso</div>
          <v-row dense>
            <v-col cols="6">
              <div class="caption grey--text">Código QR</div>
              <div class="font-weight-medium" style="word-break: break-all;">{{ selectedInvitation.qr_code || '—' }}</div>
            </v-col>
            <v-col cols="6">
              <div class="caption grey--text">PIN de acceso</div>
              <div class="title font-weight-bold">{{ selectedInvitation.code || '—' }}</div>
            </v-col>
          </v-row>

          <div v-if="selectedInvitation.custom_message" class="mt-4">
            <v-alert dense text color="primary" icon="mdi-message-text-outline">
              {{ selectedInvitation.custom_message }}
            </v-alert>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-btn
            v-if="selectedInvitation.url_map"
            text color="primary"
            :href="selectedInvitation.url_map"
            target="_blank"
          >
            <v-icon left small>mdi-map</v-icon>Ver mapa
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="detailDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import officeInvitationService from '@/services/officeInvitationService'

export default {
  name: 'oficina_invitaciones',
  data () {
    return {
      invitations: [],
      dialog: false,
      detailDialog: false,
      selectedInvitation: null,
      loading: false,
      savingInvitation: false,
      invitationTypes: [
        { text: 'Normal', value: 0 },
        { text: 'Recurrente', value: 2 }
      ],
      weekDays: [
        { label: 'Lun', value: '0' },
        { label: 'Mar', value: '1' },
        { label: 'Mié', value: '2' },
        { label: 'Jue', value: '3' },
        { label: 'Vie', value: '4' },
        { label: 'Sáb', value: '5' },
        { label: 'Dom', value: '6' }
      ],
      selectedDays: [],
      searchTimeout: null,
      invitation: this.emptyInvitation(),
      headers: [
        { text: 'Asunto', value: 'subject' },
        { text: 'Invitado', value: 'guest_name' },
        { text: 'Teléfono', value: 'guest_number_phone' },
        { text: 'Fecha', value: 'date' },
        { text: 'Hora', value: 'hour' },
        { text: 'Confirmada', value: 'confirmed' },
        { text: 'Estado', value: 'canceled' },
        { text: 'Acciones', value: 'actions', sortable: false }
      ]
    }
  },
  mounted () {
    this.loadInvitations()
  },
  methods: {
    viewDetails (item) {
      this.selectedInvitation = item
      this.detailDialog = true
    },
    formatType (type) {
      const match = this.invitationTypes.find(t => t.value === type)
      return match ? match.text : '—'
    },
    formatDiary (diary) {
      if (!diary) return '—'
      return diary.split('').map(d => {
        const match = this.weekDays.find(day => day.value === d)
        return match ? match.label : d
      }).join(', ')
    },
    emptyGuest () {
      return {
        fullNameGuest: '',
        email: '',
        cellNumber: '',
        _searchText: '',
        _searchResults: [],
        _searching: false,
        _isExistingUser: false,
        _avatar: null
      }
    },
    emptyInvitation () {
      return {
        subject: '',
        typeInv: 0,
        dateInv: '',
        timeInv: '09:00',
        expiration: null,
        diary: '',
        vehicle: false,
        supplier: false,
        notes: '',
        guests: [this.emptyGuest()]
      }
    },
    addGuest () {
      this.invitation.guests.push(this.emptyGuest())
    },
    removeGuest (index) {
      this.invitation.guests.splice(index, 1)
    },
    updateDiary () {
      this.invitation.diary = [...this.selectedDays].sort().join('')
    },
    onGuestSearchInput (index, query) {
      const guest = this.invitation.guests[index]
      clearTimeout(this.searchTimeout)
      if (!query || query.length < 3) {
        guest._searchResults = []
        return
      }
      this.searchTimeout = setTimeout(async () => {
        guest._searching = true
        try {
          guest._searchResults = await officeInvitationService.searchUsers(query)
        } catch (error) {
          guest._searchResults = []
        } finally {
          guest._searching = false
        }
      }, 400)
    },
    selectExistingUser (index, user) {
      const guest = this.invitation.guests[index]
      guest.fullNameGuest = `${user.first_name} ${user.last_name}`.trim()
      guest.email = user.email || ''
      guest.cellNumber = user.cellNumber || ''
      guest._avatar = user.avatar || null
      guest._isExistingUser = true
      guest._searchResults = []
    },
    clearGuestSearch (index) {
      const guest = this.invitation.guests[index]
      guest._searchText = ''
      guest._searchResults = []
    },
    clearExistingUser (index) {
      const guest = this.invitation.guests[index]
      guest.fullNameGuest = ''
      guest.email = ''
      guest.cellNumber = ''
      guest._avatar = null
      guest._isExistingUser = false
      guest._searchResults = []
      guest._searchText = ''
    },
    async loadInvitations () {
      this.loading = true
      try {
        const response = await officeInvitationService.getInvitations()
        this.invitations = Array.isArray(response) ? response : response.results || []
      } catch (error) {
        this.notify(error.message, 'error')
      } finally {
        this.loading = false
      }
    },
    openDialog () {
      this.invitation = this.emptyInvitation()
      this.selectedDays = []
      this.dialog = true
    },
    async saveInvitation () {
      if (!this.$refs.form.validate()) return
      if (this.invitation.guests.length === 0 || !this.invitation.guests[0].fullNameGuest) {
        this.notify('Debes agregar al menos un invitado', 'error')
        return
      }
      if (this.invitation.typeInv === 2 && (!this.invitation.expiration || !this.invitation.diary)) {
        this.notify('Las invitaciones recurrentes requieren fecha de expiración y al menos un día seleccionado', 'error')
        return
      }

      this.savingInvitation = true
      try {
        const payload = {
          subject: this.invitation.subject,
          typeInv: this.invitation.typeInv,
          dateInv: this.invitation.dateInv,
          timeInv: this.invitation.timeInv,
          expiration: this.invitation.expiration || null,
          diary: this.invitation.diary || '',
          vehicle: this.invitation.vehicle,
          supplier: this.invitation.supplier,
          notes: this.invitation.notes,
          guests: this.invitation.guests.map(guest => ({
            fullNameGuest: guest.fullNameGuest,
            email: guest.email,
            cellNumber: guest.cellNumber
          }))
        }

        await officeInvitationService.createInvitation(payload)
        this.dialog = false
        this.notify('Invitación enviada correctamente')
        this.loadInvitations()
      } catch (error) {
        this.notify(error.message, 'error')
      } finally {
        this.savingInvitation = false
      }
    },
    notify (message, color = 'success') {
      this.$store.commit('snackbar_change_color', color)
      this.$store.commit('snackbar_change_message', message)
      this.$store.commit('snackbar_activate_true')
    }
  }
}
</script>
